import {
  FETCH_WELCOMES_REQUEST,
  FETCH_WELCOMES_SUCCESS,
  FETCH_WELCOMES_FAIL,
  SAVE_WELCOME_REQUEST,
  SAVE_WELCOME_SUCCESS,
  SAVE_WELCOME_FAIL,
  DELETE_WELCOME_SUCCESS,
  WELCOME_TYPE_CHANGE,
  PUBLISH_WELCOME,
  UNPUBLISH_WELCOME,
  LOAD_WELCOME,
  CLEAR_WELCOME_STATE,
  SORT_WELCOME_TYPE_CHANGE,
  SEARCH_WELCOME,
  CHANGE_WELCOME_PAGE,
  CLEAR_SEARCH,
} from 'actions/Welcomes/types'
import { MESSAGE_DESTINATION_OPTIONS } from 'config/constants'

export const defaultState = {
  isLoading: false,
  welcomes: [],
  filteredWelcomes: [],
  welcomeType: { value: '', label: 'Choose a type...' },
  welcomeTypeDisabled: false,
  currentWelcome: null,
  isSearching: false,
  sortType: 'updated',
  currentPage: 1,
}

export default (state = defaultState, action) => {
  switch (action.type) {
    case CLEAR_SEARCH:
      return {
        ...state,
        isSearching: false,
        filteredWelcomes: [],
      }
    case CLEAR_WELCOME_STATE:
      return {
        ...defaultState,
        currentPage: state.currentPage,
      }
    case WELCOME_TYPE_CHANGE:
      return {
        ...state,
        welcomeType: action.welcomeType,
      }
    case FETCH_WELCOMES_REQUEST:
      return {
        ...state,
        isLoading: true,
      }
    case FETCH_WELCOMES_SUCCESS:
      return {
        ...state,
        welcomes: action.welcomes,
        isLoading: false,
      }
    case FETCH_WELCOMES_FAIL:
      return {
        ...state,
        isLoading: false,
      }
    case SAVE_WELCOME_REQUEST:
      return {
        ...state,
        isLoading: true,
      }
    case SAVE_WELCOME_SUCCESS:
      return {
        ...state,
        isLoading: false,
      }
    case SAVE_WELCOME_FAIL:
      return {
        ...state,
        isLoading: false,
      }
    case DELETE_WELCOME_SUCCESS:
      const welcomeToDeleteIndex = state.welcomes.findIndex(
        welcome => action.welcomeId === welcome.id,
      )
      const filteredWelcomeToDeleteIndex = state.filteredWelcomes.findIndex(
        welcome => action.welcomeId === welcome.id,
      )

      return {
        ...state,
        welcomes: [
          ...state.welcomes.slice(0, welcomeToDeleteIndex),
          ...state.welcomes.slice(welcomeToDeleteIndex + 1),
        ],
        filteredWelcomes: [
          ...state.filteredWelcomes.slice(0, filteredWelcomeToDeleteIndex),
          ...state.filteredWelcomes.slice(filteredWelcomeToDeleteIndex + 1),
        ],
      }
    case PUBLISH_WELCOME:
      const indexToPublish = state.welcomes.findIndex(
        welcome => welcome.id === action.welcomeId,
      )

      const publishedPost = {
        ...state.welcomes[indexToPublish],
        active: true,
      }

      return {
        ...state,
        welcomes: [
          ...state.welcomes.slice(0, indexToPublish),
          publishedPost,
          ...state.welcomes.slice(indexToPublish + 1),
        ],
      }
    case UNPUBLISH_WELCOME:
      const indexToUnpublish = state.welcomes.findIndex(
        welcome => welcome.id === action.welcomeId,
      )

      const unpublishedPost = {
        ...state.welcomes[indexToUnpublish],
        active: false,
      }

      return {
        ...state,
        welcomes: [
          ...state.welcomes.slice(0, indexToUnpublish),
          unpublishedPost,
          ...state.welcomes.slice(indexToUnpublish + 1),
        ],
      }
    case LOAD_WELCOME:
      const currentWelcome = state.welcomes.find(
        welcome => welcome.id === action.welcomeId,
      )

      return {
        ...state,
        currentWelcome,
        welcomeTypeDisabled: true,
        welcomeType: determineWelcomeType(currentWelcome.ctaAction),
      }
    case SORT_WELCOME_TYPE_CHANGE:
      return {
        ...state,
        welcomes: sortWelcomes(state.welcomes, action.sortType),
        sortType: action.sortType,
      }
    case SEARCH_WELCOME:
      const { searchString } = action
      const isSearching = searchString !== ''

      const filteredByInfo = filterByInfo(state.welcomes, searchString)
      const filteredByCtaText = filterByCtaText(state.welcomes, searchString)
      const filteredWelcomes = mergeFilteredWelcomes(
        filteredByCtaText,
        filteredByInfo,
      )

      return {
        ...state,
        filteredWelcomes,
        isSearching,
      }
    case CHANGE_WELCOME_PAGE:
      return {
        ...state,
        currentPage: action.page,
      }
    default:
      return state
  }
}

function determineWelcomeType(ctaAction) {
  const option = MESSAGE_DESTINATION_OPTIONS.find(
    option => option.value === ctaAction,
  )
  if (option) {
    return option
  }
  return MESSAGE_DESTINATION_OPTIONS.find(option => option.value === 'no_cta')
}

function getType(ctaAction) {
  switch (ctaAction) {
    case 'newLog':
      return 'Log: New'
    case 'firstLog':
      return 'Log: First'
    case 'poll':
      return 'Poll'
    case 'insight':
      return 'Insights: Personal'
    case 'insight_community':
      return 'Insights: Community'
    case 'bookmarks':
      return 'Learn: My Saved Content'
    case 'recommended':
      return 'Learn: Recommended'
    case 'profile':
      return 'Profile: General'
    case 'profile_zip':
      return 'Profile: Zip'
    case 'profile_phone':
      return 'Profile: Phone'
    case 'profile_phys':
      return 'Profile: Physician'
    case 'profile_supp':
      return 'Profile: Support'
    case 'history':
      return 'History'
    case 'shareReport':
      return 'Share: Report'
    case 'shareApp':
      return 'Share: App'
    case 'giveFeedback':
      return 'Share: Feedback'
    case 'no_cta':
    default:
      return 'No CTA'
  }
}

function sortWelcomes(welcomes, sortType) {
  switch (sortType) {
    case 'updated':
      return welcomes.sort((a, b) => b.updated - a.updated)
    case 'updated-ascending':
      return welcomes.sort((a, b) => a.updated - b.updated)
    case 'created':
      return welcomes.sort((a, b) => b.created - a.created)
    case 'created-ascending':
      return welcomes.sort((a, b) => a.created - b.created)
    case 'published':
      return welcomes.sort(
        (a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1),
      )
    case 'unpublished':
      return welcomes.sort(
        (a, b) => (a.active === b.active ? 0 : a.active ? 1 : -1),
      )
    case 'type':
      return welcomes.sort((a, b) =>
        getType(b.ctaAction).localeCompare(getType(a.ctaAction)),
      )
    case 'type-ascending':
      return welcomes.sort((a, b) =>
        getType(a.ctaAction).localeCompare(getType(b.ctaAction)),
      )
    default:
      return welcomes
  }
}

function filterByInfo(welcomes, searchString) {
  const searchTerms = searchString.toLowerCase()
  return welcomes.filter(welcome => {
    return welcome.info.toLowerCase().search(searchTerms) !== -1
  })
}

function filterByCtaText(welcomes, searchString) {
  const searchTerms = searchString.toLowerCase()
  return welcomes.filter(welcome => {
    if (welcome.ctaText) {
      return welcome.ctaText.toLowerCase().search(searchTerms) !== -1
    } else {
      return false
    }
  })
}

function mergeFilteredWelcomes(welcomesOne, welcomesTwo) {
  return welcomesOne.concat(
    welcomesTwo.filter(p2 => !welcomesOne.some(p1 => p1.id === p2.id)),
  )
}
